import { CSSResultGroup, html, nothing, PropertyValues } from 'lit'
import { customElement, property, query, state } from 'lit/decorators.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { style } from './textarea.style.js'
import { ValueFormElement } from 'elements/_shared/formElement.js'
import { classMap } from 'lit/directives/class-map.js'
import { formFieldValidationTypes } from 'elements/form-field/form-field.js'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'


export const textAreaResizeProperties = ['vertical', 'none'] as const
export type textAreaResizePropertyType = typeof textAreaResizeProperties

@customElement('cse-textarea')
export class CSETextarea extends ValueFormElement {
  static override styles: CSSResultGroup = [hostStyle, style]

  @property({ type: String, attribute: 'id' })
  _id = ''

  @property({ type: String })
  name = ''

  @property({ type: String, reflect: true })
  value = ''

  @property({ type: Boolean })
  required = false

  @property({ type: Boolean })
  disabled = false

  @property({ type: Boolean })
  override autofocus: boolean = false

  @property({ type: String })
  placeholder = ''

  @property({ type: String })
  resize: textAreaResizePropertyType[number] = 'vertical'

  @property({ type: String })
  valid: formFieldValidationTypes[number] = 'none'

  @property({ type: Number })
  rows = 3

  @property({ type: Number })
  maxlength? = 0

  @query('textarea.cse-textarea')
  textarea!: HTMLTextAreaElement

  @property({ type: String, attribute: 'required-text' })
  requiredText?: string

  @state()
  private typeCount = 0

  private getTrimmedValue = (value: string) => {
    if (this.maxlength) {
      return value.slice(0, this.maxlength)
    }

    return value
  }

  private handleChange = () => {
    this.value = this.getTrimmedValue(this.textarea.value)
    this.emit(this.value)
  }

  private handleInput = () => {
    this.typeCount = this.getTrimmedValue(this.textarea.value).length
  }

  private checkRequired() {
    if (this.required && this.value.trim() === '') {
      this.internals.setValidity({
        valueMissing: true
      }, this.requiredText || 'This field is required', this.textarea)
    } else {
      this.internals.setValidity({})
    }
  }

  private renderCounter = () => {
    return this.maxlength && this.maxlength > 0 ? html`
        <cse-text variant="caption-m">
          ${this.typeCount}/${this.maxlength}
        </cse-text>
    `: nothing
  }

  override focus() {
    this.textarea.focus()
  }

  protected override updated(changedProperties: PropertyValues) {
    super.updated(changedProperties)

    if (changedProperties.has('value')) {
      this.checkRequired()
    }
  }

  override connectedCallback() {
    super.connectedCallback()

    this.typeCount = this.value.length
  }

  override render() {
    return html`
      <textarea
        class="${classMap({
      'cse-textarea': true,
      [`valid-${this.valid}`]: true,
      'resize-none': this.resize === 'none',
    })}"
        id="${this._id}"
        name="${this.name}"
        rows="${this.rows}"
        maxlength="${ifDefined(this.maxlength || undefined)}"
        ?required="${this.required}"
        ?disabled="${this.disabled}"
        ?autofocus="${this.autofocus}"
        placeholder="${this.placeholder}"
        @change="${this.handleChange}"
        @input="${this.handleInput}"
      >${this.value}</textarea>
      ${this.renderCounter()}
    `
  }
}
declare global {
  interface HTMLElementTagNameMap {
    'cse-textarea': CSETextarea
  }
}
